import React, { useEffect, useRef } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import * as api from '../../services/teacherApi';
import { attemptRow, DataFigure, fractionRow } from '../../components/teacherAnalytics/AnalyticsFigures';
import {
  AnalyticsGate, CompletionView, CourseShell, DateView, FractionView, LoadState, MeasureView,
  Metrics, Pager, Panel, Placements, PlainText, presenceCopy, Reasons, Records, RestoreFocus,
  safeReturn, StudentIdentity, useAnalytics, useQueryState, ForegroundTimeView
} from '../../components/teacherAnalytics/AnalyticsShared';

interface Scope {courseId: string; studentId: string}
interface TopicScope extends Scope {topicId: string; title?: string | null}
function Chapters({courseId, studentId}: Scope) {
  const {pageNumber, update} = useQueryState();
  const page = pageNumber();
  const resource = useAnalytics(`${courseId}/${studentId}/chapters/${page}`,
    signal => api.getStudentAnalyticsChapters(courseId, studentId, {page, limit: 20}, signal));
  return <Panel><h2 data-results-heading tabIndex={-1}>章节进度</h2>
    <LoadState resource={resource}>{data => <>
      <RestoreFocus ready />
      {!data.chapters.items.length && <p>{page > 1 ? '当前页暂无记录。' : '此课程暂无章节。'}</p>}
      <DataFigure title="章节完成进度图（本页）" scale="percent"
        caption={`第 ${data.chapters.pagination.page} 页章节`}
        rows={data.chapters.items.map(c => fractionRow(c.chapterId, c.title || '未命名章节', c.topics))} />
      <Records>{data.chapters.items.map(c => <article key={c.chapterId}>
        <h3>{c.title || '未命名章节'}</h3>
        {!c.topics.total ? <p>暂无知识元 · 不计入完成章节总数</p> : <FractionView value={c.topics} unit="个" />}
        <div><CompletionView value={c.completion} /></div>
      </article>)}</Records>
      <Pager pagination={data.chapters.pagination} onPage={p => update({page: p})} />
    </>}</LoadState>
  </Panel>;
}
function History({courseId, studentId, topicId}: TopicScope) {
  const {params, pageNumber, update} = useQueryState();
  const page = pageNumber('historyPage');
  const activityId = params.get('activityId') || '';
  const exerciseId = params.get('exerciseId') || '';
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => heading.current?.focus(), [activityId, exerciseId]);
  const resource = useAnalytics(`${courseId}/${studentId}/${topicId}/history/${activityId}/${exerciseId}/${page}`,
    signal => api.getStudentTopicAttempts(courseId, studentId, topicId, {page, limit: 20, activityId, exerciseId}, signal));
  return <Panel id="retained-attempts" aria-label="已保留尝试"><h3 id="history-heading" ref={heading} tabIndex={-1}>已保留尝试 · {exerciseId}</h3>
    <button onClick={() => update({activityId: null, exerciseId: null, historyPage: null}, `exercise-${activityId}/${exerciseId}`)}>收起尝试明细</button>
    <LoadState resource={resource}>{data => <>
      <RestoreFocus ready onlyRequested />
      <div>已记录练习次数：<MeasureView measure={data.history} unit=" 次" /></div>
      {!data.attempts.items.length && <p>暂无可用的尝试明细。</p>}
      <Records>{data.attempts.items.map(a => <article key={a.attemptKey}><dl>
        <div><dt>尝试序号</dt><dd>{a.attemptNumber}</dd></div>
        <div><dt>记录时间</dt><dd><DateView value={a.answeredAt} /></dd></div>
        <div><dt>练习用时</dt><dd><MeasureView measure={a.exerciseTimeSeconds} unit=" 秒" /></dd></div>
      </dl></article>)}</Records>
      <Pager pagination={data.attempts.pagination} onPage={p => update({historyPage: p}, 'history-heading')} />
    </>}</LoadState>
  </Panel>;
}
function Exercises(scope: TopicScope) {
  const {courseId, studentId, topicId} = scope;
  const {params, pageNumber, update} = useQueryState();
  const page = pageNumber('exercisePage');
  const resource = useAnalytics(`${courseId}/${studentId}/${topicId}/exercises/${page}`,
    signal => api.getStudentTopicExercises(courseId, studentId, topicId, {page, limit: 20}, signal));
  return <Panel><h3 id="exercise-heading" tabIndex={-1}>练习记录</h3>
    <LoadState resource={resource}>{data => <>
      {!data.exercises.items.length && <p>{page > 1 ? '当前页暂无记录。' : '暂无本课程该知识元的练习记录，无法据此判断是否练习过。'}</p>}
      {!params.get('activityId') && <RestoreFocus ready onlyRequested />}
      <DataFigure title="已记录练习次数对比（本页）" scale="count"
        caption={`第 ${data.exercises.pagination.page} 页练习 · 单位：次`}
        rows={data.exercises.items.map(e => attemptRow(JSON.stringify([e.activityId, e.exerciseId]),
          `练习：${e.exerciseId} · 活动：${e.activityId}`, e.attempts))} />
      <Records>{data.exercises.items.map(e => <article key={JSON.stringify([e.activityId, e.exerciseId])}>
        <h3>练习标识：{e.exerciseId}</h3><small>活动标识：{e.activityId}</small>
        <dl><div><dt>已记录练习次数</dt><dd><MeasureView measure={e.attempts} unit=" 次" /></dd></div>
          <div><dt>练习用时</dt><dd><MeasureView measure={e.exerciseTimeSeconds} unit=" 秒" /></dd></div>
          <div><dt>首次记录时间</dt><dd><DateView value={e.firstRecordedAt} /></dd></div>
          <div><dt>最近记录时间</dt><dd><DateView value={e.lastRecordedAt} /></dd></div>
        </dl>
        <button id={`exercise-${e.activityId}/${e.exerciseId}`} aria-controls="retained-attempts" aria-expanded={params.get('activityId') === e.activityId && params.get('exerciseId') === e.exerciseId}
          onClick={() => update({activityId: e.activityId, exerciseId: e.exerciseId, historyPage: 1})}>查看已保留尝试</button>
      </article>)}</Records>
      <Pager pagination={data.exercises.pagination} onPage={p => update({exercisePage: p}, 'exercise-heading')} />
    </>}</LoadState>
    {params.get('activityId') && params.get('exerciseId') && <History {...scope} />}
  </Panel>;
}
function Explanations({courseId, studentId, topicId}: TopicScope) {
  const {pageNumber, update} = useQueryState();
  const page = pageNumber('explanationPage');
  const {params} = useQueryState();
  const limit = Number(params.get('explanationLimit') || 20);
  const resource = useAnalytics(`${courseId}/${studentId}/${topicId}/explanations/${page}/${limit}`,
    signal => api.getStudentTopicExplanations(courseId, studentId, topicId, {page, limit}, signal));
  return <Panel><h3 id="explanation-heading" tabIndex={-1}>自我解释（本课程）</h3>
    <LoadState resource={resource} label="正在加载自我解释…">{data => <>
      <RestoreFocus ready onlyRequested />
      {!data.explanations.items.length && <p>{page > 1 ? '当前页暂无记录。' : '暂无本课程该知识元的自我解释记录。'}</p>}
      <Records>{data.explanations.items.map(e => <article key={e.activityId}>
        <h3>活动标识：{e.activityId}</h3><p>来源：本课程活动</p>
        <small>创建时间：<DateView value={e.createdAt} /> · 更新时间：<DateView value={e.updatedAt} /></small>
        {e.availability === 'partial' && <small>版本有差异 · 显示最新记录</small>}
        <Reasons reasons={e.reasons} /><PlainText>{e.text}</PlainText>
      </article>)}</Records>
      <Pager pagination={data.explanations.pagination} onPage={p => update({explanationPage: p}, 'explanation-heading')} />
    </>}</LoadState>
    {resource.error?.code === 'ANALYTICS_SCOPE_TOO_LARGE' && (limit === 1
      ? <p>单条解释内容过大，暂时无法显示。</p>
      : <button onClick={() => update({explanationPage: 1, explanationLimit: 1})}>每页显示 1 条后重试</button>)}
  </Panel>;
}
function TopicDetail(scope: TopicScope) {
  const {update} = useQueryState();
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => heading.current?.focus(), [scope.topicId]);
  return <Panel id={`topic-detail-${scope.topicId}`} aria-label="练习与解释"><h2 ref={heading} tabIndex={-1}>{scope.title || `知识元：${scope.topicId}`} · 练习与解释</h2>
    <button onClick={() => update({topicId: null, activityId: null, exerciseId: null, exercisePage: null, historyPage: null, explanationPage: null, explanationLimit: null}, `topic-${scope.topicId}`)}>收起详情</button>
    <Exercises {...scope} /><Explanations {...scope} />
  </Panel>;
}
function Topics(scope: Scope) {
  const {courseId, studentId} = scope;
  const {params, pageNumber, update} = useQueryState();
  const page = pageNumber();
  const topicId = params.get('topicId') || '';
  const resource = useAnalytics(`${courseId}/${studentId}/topics/${page}`,
    signal => api.getStudentAnalyticsTopics(courseId, studentId, {page, limit: 20}, signal));
  return <Panel><h2 data-results-heading tabIndex={-1}>知识元记录</h2>
    {topicId && !resource.loading && !resource.result?.data.topics.items.some(t => t.topicId === topicId) &&
      <TopicDetail {...scope} topicId={topicId} />}
    <LoadState resource={resource}>{data => <>
      {!topicId && <RestoreFocus ready />}
      {!data.topics.items.length && <p>{page > 1 ? '当前页暂无记录。' : '此课程暂无知识元。'}</p>}
      <DataFigure title="知识元练习次数对比（本页）" scale="count"
        caption={`第 ${data.topics.pagination.page} 页知识元`}
        rows={data.topics.items.map(t => attemptRow(t.topicId, t.title || `知识元：${t.topicId}`, t.attempts))} />
      <Records>{data.topics.items.map((t, i, items) => <React.Fragment key={t.topicId}>
        {(i === 0 || t.placements[0]?.chapterId !== items[i - 1].placements[0]?.chapterId ||
          t.placements[0]?.sectionId !== items[i - 1].placements[0]?.sectionId) &&
          <h3>{t.placements[0]?.chapterTitle || '未命名章节'}{t.placements[0]?.sectionTitle && ` / ${t.placements[0].sectionTitle}`}</h3>}
        <article><h3>{t.title || `知识元信息不可用 · ${t.topicId}`}</h3><Placements topic={t} />
          <ForegroundTimeView value={t.foregroundElapsed} />
          {!!t.legacyDurationEvidence?.records && <p>历史原值（待核实）：{t.legacyDurationEvidence.minRaw}–{t.legacyDurationEvidence.maxRaw}
            （{t.legacyDurationEvidence.records} 条；正值 {t.legacyDurationEvidence.positive} 条，零值 {t.legacyDurationEvidence.zero} 条）。
            </p>}<dl>
          <div><dt>完成状态</dt><dd><CompletionView value={t.completion} /></dd></div>
          <div><dt>已记录练习次数</dt><dd><MeasureView measure={t.attempts} unit=" 次" /></dd></div>
          <div><dt>练习用时</dt><dd><MeasureView measure={t.exerciseTimeSeconds} unit=" 秒" /></dd></div>
          <div><dt>自我解释（本课程）</dt><dd>{presenceCopy(t.courseExplanationPresent)}</dd></div>
        </dl><button id={`topic-${t.topicId}`} aria-controls={`topic-detail-${t.topicId}`} aria-expanded={topicId === t.topicId} onClick={() => update({topicId: t.topicId, activityId: null, exerciseId: null,
          exercisePage: null, historyPage: null, explanationPage: null, explanationLimit: null})}>查看练习与解释</button></article>
        {topicId === t.topicId && <TopicDetail {...scope} topicId={topicId} title={t.title} />}
      </React.Fragment>)}</Records>
      <Pager pagination={data.topics.pagination} onPage={p => update({page: p})} />
    </>}</LoadState>
  </Panel>;
}
function StudentDetail() {
  const {courseId = '', studentId = ''} = useParams();
  const {params} = useQueryState();
  const location = useLocation();
  const tab = params.get('tab') || 'chapters';
  const topicId = params.get('topicId') || undefined;
  const resource = useAnalytics(`${courseId}/${studentId}/summary`, signal => api.getStudentAnalytics(courseId, studentId, signal));
  const state = location.state as {returnTo?: string; focusId?: string; scrollY?: number; courseListPage?: number} | null;
  const returnTo = safeReturn(courseId, state?.returnTo, topicId);
  const returnLabel = returnTo.split('?')[0] === `/teacher/courses/${courseId}/analytics` ? '返回知识元表现' : '返回学生列表';
  const result = resource.result?.data;
  return <CourseShell course={result?.course} title={result ? `${result.student.name || '未命名学生'} · 学习详情` : '学习详情'}>
    <nav aria-label="返回列表"><Link to={returnTo}
      state={{focusId: state?.focusId, scrollY: state?.scrollY, courseListPage: state?.courseListPage}}>{returnLabel}</Link></nav>
    <LoadState resource={resource}>{data => <>
      <Panel><StudentIdentity student={data.student} />
        <p>注册时间：<DateView value={data.student.enrolledAt} enrollment /></p>
        <DataFigure title="学生学习进度图" scale="percent" caption="当前课程 · 章节与知识元"
          rows={[fractionRow('chapters', '已记录完成章节', data.student.chapters), fractionRow('topics', '已记录完成知识元', data.student.topics)]} />
        <Metrics>
          <div><h3>已记录完成章节</h3><FractionView value={data.student.chapters} unit="章" /></div>
          <div><h3>已记录完成知识元</h3><FractionView value={data.student.topics} unit="个" /></div>
          <div><h3>已记录练习次数</h3><MeasureView measure={data.student.attempts} unit=" 次" /></div>
          <div><h3>练习用时</h3><MeasureView measure={data.student.exerciseTimeSeconds} unit=" 秒" /></div>
        </Metrics>
        <ForegroundTimeView value={data.student.foregroundElapsed} />
      </Panel>
    </>}</LoadState>
    <nav aria-label="学生记录"><Link to="?tab=chapters&page=1" aria-current={tab === 'chapters' ? 'page' : undefined} state={state}>章节进度</Link>
      <Link to="?tab=topics&page=1" aria-current={tab === 'topics' ? 'page' : undefined} state={state}>知识元记录</Link></nav>
    {tab === 'chapters' ? <Chapters courseId={courseId} studentId={studentId} /> :
      tab === 'topics' ? <Topics courseId={courseId} studentId={studentId} /> :
        <Panel role="alert">链接或查询条件无效，请返回列表重试。</Panel>}
  </CourseShell>;
}
export const StudentLearningDetailPage = () => <AnalyticsGate><StudentDetail /></AnalyticsGate>;
